<template>
    <div class="personal-goal-overview">
        <div class="row">
            <div class="col-12 col-md-6">
                <div class="apprentice-simple-card personal-goals-overview">
                    <div class="component-title">
                        <h5 class="title">{{ $t('common.learning_goals') }}</h5>

                        <div
                            class="actions"
                            v-if="!teacherAccess">
                            <b-button
                                class="btn-add btn-add-personal-goal"
                                v-b-tooltip.hover
                                variant="primary-icon-only"
                                :title="$t('common.add_learning_goal')"
                                @click="addPersonalGoal">
                            </b-button>
                        </div>
                    </div>

                    <div
                        class="not-found with-header"
                        v-if="personalGoals.length === 0">
                        <div
                            class="btn-add-circle icon"
                            v-b-tooltip.hover
                            v-if="!teacherAccess"
                            :title="$t('common.add_learning_goal')"
                            @click="addPersonalGoal"></div>
                        <div
                            class="alert-notice"
                            v-if="!teacherAccess">
                            {{ $t('personal_goals.you_do_not_have_a_learning_goal_yet') }}
                        </div>
                        <div
                            class="alert-notice"
                            v-if="teacherAccess">
                            {{ $t('personal_goals.no_learning_goals_have_been_set') }}
                        </div>
                    </div>

                    <design-2021-personal-goal-component
                        v-for="goal in personalGoals"
                        v-else
                        :personal-goal-in="goal"
                        :teacher-access="teacherAccess"
                        :skill-trees-in="skillsIn"
                        :categories-in="categoriesIn"
                        :seasons-in="seasonsIn"
                        :key="`${goal.id}-overviewed-${goal.name.toLowerCase()}`"
                        @personal-goal-overview="showPersonalGoalOverview"
                        @new-profile-card-from-personal-goal="createProfileCardFromPersonalGoal">
                    </design-2021-personal-goal-component>
                </div>
            </div>
            <div class="col-12 col-md-6">
                <div class="apprentice-simple-card personal-goals-archived-overview">
                    <div class="component-title">
                        <h5 class="title">{{ $t('personal_goals.archived_learning_goals') }}</h5>
                    </div>

                    <div
                        class="not-found with-header empty-personal-goals-archived-list"
                        v-if="archivedPersonalGoals.length === 0">
                        <div class="icon">
                            <i class="fas fa-exclamation-circle"></i>
                        </div>
                        <div class="alert-notice">{{ $t('personal_goals.no_archived_learning_goals') }}</div>
                    </div>

                    <design-2021-personal-goal-archived-component
                        v-for="goal in archivedPersonalGoals"
                        v-else
                        :personal-goal-in="goal"
                        :teacher-access="teacherAccess"
                        :skill-trees-in="skillsIn"
                        :key="`${goal.id}-archived-${goal.name.toLowerCase()}`"
                        @personal-goal-overview="showPersonalGoalOverview">
                    </design-2021-personal-goal-archived-component>
                </div>
            </div>
        </div>

        <design-2021-personal-goal-overview-modal-component
            :skill-trees-in="skillsIn"
            :teacher-access="teacherAccess"
            :badges-in="badges"
            ref="personal-goal-overview-modal"
            @personal-goal-updated="updatedPersonalGoal"
            @personal-goal-tasks-removed="removePersonalGoalTask"
            @show-profile-card-from-personal-goal-modal="createProfileCardFromPersonalGoal">
        </design-2021-personal-goal-overview-modal-component>

        <design-2021-personal-goal-add-personal-goal-modal-component
            ref="personal-goal-add-modal"
            :skill-trees-in="skillsIn"
            @add-task="addPersonalGoalTask">
        </design-2021-personal-goal-add-personal-goal-modal-component>

        <design-2021-personal-goal-add-task-modal-component ref="add-personal-goal-task">
        </design-2021-personal-goal-add-task-modal-component>

        <design-2021-profile-add-card-component
            ref="addProfileCard"
            :categories-in="categoriesIn"
            :seasons-in="seasonsIn"
            @add-card="addCard">
        </design-2021-profile-add-card-component>
    </div>
</template>

<script>
import { ords } from '../../app';

const personalGoalsOrds = ords.use('personalGoal');
const badgesOrds = ords.use('badges');

export default {
    name: 'design-2021-personal-goal-index-component',
    props: {
        personalGoalsIn: {
            type: Array,
            required: true,
            default: () => [],
        },
        userIn: {
            type: Object,
            default: null,
        },
        skillsIn: {
            type: Array,
            required: true,
            default: () => [],
        },
        teacherAccess: {
            type: Number,
            required: false,
            default: 0,
        },
        badgesIn: {
            type: Array,
            required: true,
            default: 0,
        },
        categoriesIn: {
            type: Array,
            required: true,
            default: () => [],
        },
        seasonsIn: {
            type: Array,
            required: true,
            default: () => [],
        },
    },

    data() {
        return {
            goals: [],
            badges: [],
        };
    },

    mounted() {
        personalGoalsOrds.add(this.personalGoalsIn);
        badgesOrds.add(this.badgesIn);

        personalGoalsOrds
            .with('tasks', (taskCallback) => {
                taskCallback.with('files');
                taskCallback.with('personal_goal_task_deadline', (deadlineCallback) => {
                    deadlineCallback.with('agenda_item');
                });
            })
            .with('skills')
            .with('badges', (badgesCallback) => {
                badgesCallback.with('file');
            })
            .orderBy('created_at', 'desc')
            .get()
            .subscribe((personalGoals) => {
                this.goals = personalGoals;
            });

        badgesOrds
            .with('file')
            .get()
            .subscribe((badges) => {
                this.badges = badges;
            });
    },

    methods: {
        addPersonalGoal() {
            this.$refs['personal-goal-add-modal'].show();
        },

        showPersonalGoalOverview(selectedPersonalGoal) {
            const personalGoal = this.goals.find((goal) => goal.id === selectedPersonalGoal.id);
            this.$refs['personal-goal-overview-modal'].show(personalGoal);
        },

        updatedPersonalGoal(personalGoalUpdated) {
            for (let i = 0; i < this.goals.length; i++) {
                if (this.goals[i].id === personalGoalUpdated.id) {
                    this.goals[i].update(personalGoalUpdated);
                }
            }
        },

        removePersonalGoalTask(removedTask) {
            let personalGoalToBeEdit = this.goals.find((goal) => goal.id === removedTask.personal_goal_id);

            for (let i = 0; i < personalGoalToBeEdit.tasks.length; i++) {
                if (personalGoalToBeEdit.tasks[i].id === removedTask.id) {
                    personalGoalToBeEdit.tasks[i].remove();
                }
            }
        },

        addPersonalGoalTask(goal) {
            this.$refs['add-personal-goal-task'].show(goal);
        },

        addCard(cardData) {
            this.alert.saving();
            this.axios.post(this.routes.profileCard(), cardData).then(
                (response) => {
                    this.alert.success();
                    window.location.href = this.routes.profileViewCard(response.data.id);
                },
                (error) => {
                    this.alert.ajaxError(error);
                },
            );
        },

        createProfileCardFromPersonalGoal(cardData) {
            this.$refs.addProfileCard.show(cardData);
        },
    },

    computed: {
        personalGoals() {
            if (this.goals.length === 0) {
                return [];
            }

            return this.goals.filter((personalGoal) => personalGoal.archived === 0);
        },

        archivedPersonalGoals() {
            if (this.goals.length === 0) {
                return [];
            }

            return this.goals.filter((personalGoal) => personalGoal.archived);
        },
    },

    watch: {
        goal: {
            handler: function (newVal) {
                this.goal = newVal;
            },
            deep: true,
        },
    },
};
</script>

<style scoped lang="scss">
div.personal-goals-overview ::v-deep,
div.personal-goals-archived-overview ::v-deep {
    max-height: 850px;
    overflow-y: auto;

    .component-title {
        border-bottom: 3px dotted $border-color;
        padding-bottom: map-get($grid-gutter-spacing, 'five');

        .title {
            align-self: baseline;
        }
    }

    .personal-goal {
        margin: 19px 0 15px 0;
        border-radius: $border-radius-content;
    }
}
</style>
